import { useCallback, useRef } from 'react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell
} from '@etherisc/ui-kit';
import { EditableCell } from './EditableCell';
import type { StrikeExhaust } from '../../types/product';

interface CoverageLevelGlobalLimitsCardProps {
  limits: Record<string, StrikeExhaust>; // Per-peril limits
  onChange: (limits: Record<string, StrikeExhaust>) => void;
  availablePerils: string[];
  readOnly?: boolean;
}

export function CoverageLevelGlobalLimitsCard({
  limits,
  onChange,
  availablePerils,
  readOnly = false
}: CoverageLevelGlobalLimitsCardProps) {

  const editableCellRefs = useRef<Record<string, HTMLElement | null>>({});

  const handlePerilLimitChange = (peril: string, type: 'strike' | 'exhaust', field: 'min' | 'max', value: number) => {
    if (readOnly) return;
    
    const currentPerilLimits = limits[peril] || {
      strike: { min: 5, max: 5 },
      exhaust: { min: 5, max: 5 }
    };
    
    const newLimits = {
      ...limits,
      [peril]: {
        ...currentPerilLimits,
        [type]: {
          ...currentPerilLimits[type],
          [field]: value
        }
      }
    };
    
    onChange(newLimits);
  };

  const handleCellNavigation = useCallback((rowIndex: number, colIndex: number, direction: 'up' | 'down' | 'left' | 'right') => {
    const totalCols = availablePerils.length;
    const totalRows = 4; // strike min, strike max, exhaust min, exhaust max
    
    let newRow = rowIndex;
    let newCol = colIndex;
    
    switch (direction) {
      case 'up':
        newRow = Math.max(0, rowIndex - 1);
        break;
      case 'down':
        newRow = Math.min(totalRows - 1, rowIndex + 1);
        break;
      case 'left':
        newCol = Math.max(0, colIndex - 1);
        break;
      case 'right':
        newCol = Math.min(totalCols - 1, colIndex + 1);
        break;
    }
    
    const newCellKey = `${newRow}-${newCol}`;
    const cellRef = editableCellRefs.current[newCellKey];
    if (cellRef) {
      cellRef.focus();
    }
  }, [availablePerils.length]);

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>Coverage Level Global Limits</CardTitle>
        <p className="text-sm text-gray-600 mt-2">
          Define per-peril strike and exhaust limits. Strike values control coverage triggers, exhaust values control maximum coverage levels.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {availablePerils.length > 0 ? (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-32">Limit Type</TableHead>
                  {availablePerils.map(peril => (
                    <TableHead key={peril} className="text-center min-w-24">
                      {peril}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {[
                  { type: 'strike', field: 'min', label: 'Strike Min' },
                  { type: 'strike', field: 'max', label: 'Strike Max' },
                  { type: 'exhaust', field: 'min', label: 'Exhaust Min' },
                  { type: 'exhaust', field: 'max', label: 'Exhaust Max' }
                ].map((row, rowIndex) => (
                  <TableRow key={`${row.type}-${row.field}`}>
                    <TableCell className="font-medium">{row.label}</TableCell>
                    {availablePerils.map((peril, colIndex) => {
                      const cellKey = `${rowIndex}-${colIndex}`;
                      const currentValue = limits[peril]?.[row.type as 'strike' | 'exhaust']?.[row.field as 'min' | 'max'] || 5;
                      
                      return (
                        <TableCell key={`${peril}-${row.type}-${row.field}`} className="text-center p-2">
                          <div className="flex justify-center">
                            <EditableCell
                              ref={(el) => {
                                editableCellRefs.current[cellKey] = el;
                              }}
                              value={currentValue}
                              onChange={(newValue) => 
                                handlePerilLimitChange(peril, row.type as 'strike' | 'exhaust', row.field as 'min' | 'max', newValue)
                              }
                              type="number"
                              min={0}
                              max={1000}
                              step={1}
                              suffix=" yrs"
                              className="w-20 text-center"
                              disabled={readOnly}
                              onNavigate={(direction) => {
                                if (direction === 'next') {
                                  handleCellNavigation(rowIndex, colIndex, 'right');
                                } else {
                                  handleCellNavigation(rowIndex, colIndex, 'left');
                                }
                              }}
                            />
                          </div>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="text-blue-700 text-sm">
              <strong>Note:</strong> No perils are configured. Please configure perils in the Coverage Configuration section first to see limit columns.
            </div>
          </div>
        )}

        {/* Information Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="text-blue-700 text-sm space-y-2">
            <p><strong>Strike vs Exhaust:</strong></p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li><strong>Strike:</strong> The threshold at which coverage is triggered (lower values = easier to trigger)</li>
              <li><strong>Exhaust:</strong> The maximum coverage limit beyond which no additional payout occurs</li>
            </ul>
            <p className="mt-2">
              <strong>Per-Peril Limits:</strong> Each peril can have its own strike and exhaust ranges, allowing for fine-tuned coverage control.
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
